/**
 * Admin — landing-page waitlist.
 *
 * Lists everyone who submitted the #join form on the marketing site
 * (the `waitlist` Firestore collection, newest first) with quick
 * copy-all-emails and CSV export for handing off to outreach. Data comes
 * from the `@admin`-gated `waitlistEntries` GraphQL query.
 */
import React, {useMemo} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import {
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
  tableHeadRowSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from '../../theme/planapusTheme';
import {useWaitlistEntries} from '../../api/graphql_admin_waitlist';
import {usePageTitle} from '../../hooks/usePageTitle';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function AdminWaitlist({client}: Props): React.JSX.Element {
  usePageTitle('Admin – Waitlist');
  const {data, loading, error} = useWaitlistEntries(client);
  const entries = useMemo(() => data?.waitlistEntries ?? [], [data]);

  const copyEmails = (): void => {
    void navigator.clipboard?.writeText(entries.map(e => e.email).join(', '));
  };

  const downloadCsv = (): void => {
    const rows = [
      ['email', 'source', 'createdAt', 'userAgent'],
      ...entries.map(e => [e.email, e.source, e.createdAt, e.userAgent]),
    ];
    const csv = rows
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'waitlist.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={pageSx}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography sx={pageTitleSx}>Waitlist</Typography>
          <Typography sx={sectionLabelSx}>
            Landing-page signups · {entries.length}
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyRounded />}
            onClick={copyEmails}
            disabled={!entries.length}
          >
            Copy emails
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadRounded />}
            onClick={downloadCsv}
            disabled={!entries.length}
          >
            CSV
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{mt: 3}}>
          {error.message}
        </Alert>
      ) : loading && !entries.length ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress size={24} />
        </Box>
      ) : !entries.length ? (
        <Paper sx={{...cardSx, mt: 3, p: 4, textAlign: 'center'}}>
          <Typography color="text.secondary">No signups yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{...cardSx, mt: 3}}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={tableHeadCellSx}>Email</TableCell>
                <TableCell sx={tableHeadCellSx}>Source</TableCell>
                <TableCell sx={tableHeadCellSx}>Signed up</TableCell>
                <TableCell sx={tableHeadCellSx}>Device</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(e => (
                <TableRow key={e.email} hover>
                  <TableCell sx={{...tableBodyCellSx, fontWeight: 600}}>{e.email}</TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <Chip label={e.source} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={tableBodyCellSx}>{fmtDate(e.createdAt)}</TableCell>
                  <TableCell
                    sx={{
                      ...tableBodyCellSx,
                      maxWidth: 320,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Tooltip title={e.userAgent || ''}>
                      <span>{e.userAgent || '—'}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
